import { FileText, FileType2, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { confirmModal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { revokePreviewUrl } from '../lib/previewUrl'
import type { UploadedTemplateFile } from '../types'

const MAX_BYTES = 10 * 1024 * 1024

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function detectKind(file: File): 'docx' | 'pdf' | null {
  const name = file.name.toLowerCase()
  if (name.endsWith('.docx') || file.type.includes('wordprocessingml')) return 'docx'
  if (name.endsWith('.pdf') || file.type === 'application/pdf') return 'pdf'
  return null
}

export function FileUploadZone({
  value,
  accept,
  onChange,
  onClear,
}: {
  value?: UploadedTemplateFile
  accept: 'docx' | 'pdf' | 'both'
  onChange: (file: UploadedTemplateFile, raw?: File) => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(list: FileList | null) {
    const file = list?.[0]
    if (!file) return
    if (file.size > MAX_BYTES) {
      toast.error(t('hr.belgeler.upload.tooLarge'))
      return
    }
    const kind = detectKind(file)
    if (!kind) {
      toast.error(t('hr.belgeler.upload.invalidType'))
      return
    }
    if (accept !== 'both' && kind !== accept) {
      toast.error(t('hr.belgeler.upload.invalidType'))
      return
    }
    revokePreviewUrl(value)
    const previewUrl = URL.createObjectURL(file)
    onChange(
      {
        name: file.name,
        sizeBytes: file.size,
        mime:
          file.type ||
          (kind === 'pdf'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'),
        kind,
        previewUrl,
      },
      file,
    )
    toast.success(t('hr.belgeler.upload.ok'))
  }

  const acceptAttr =
    accept === 'docx'
      ? '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : accept === 'pdf'
        ? '.pdf,application/pdf'
        : '.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'

  if (value) {
    const Icon = value.kind === 'pdf' ? FileText : FileType2
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-success/30 bg-success/5 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              'flex size-9 items-center justify-center rounded-lg',
              value.kind === 'pdf' ? 'bg-destructive/12 text-destructive' : 'bg-info/12 text-info',
            )}
          >
            <Icon className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{value.name}</div>
            <div className="text-[11px] text-muted-foreground">
              {value.kind.toUpperCase()} · {formatSize(value.sizeBytes)}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => inputRef.current?.click()}>
            {t('hr.belgeler.upload.replace')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('hr.belgeler.upload.remove')}
            onClick={() => {
              void confirmModal({
                title: t('confirm.uploadRemove.title'),
                description: t('confirm.uploadRemove.body', { name: value.name }),
                confirmLabel: t('hr.belgeler.upload.remove'),
                destructive: true,
              }).then((ok) => {
                if (!ok) return
                revokePreviewUrl(value)
                onClear()
              })
            }}
          >
            <Trash2 />
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={acceptAttr}
          onChange={(e) => {
            handleFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
        dragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/50',
      )}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        handleFiles(e.dataTransfer.files)
      }}
    >
      <span className="flex size-8 items-center justify-center rounded-lg bg-card text-muted-foreground shadow-xs sm:size-10 sm:rounded-xl">
        <Upload className="size-4" />
      </span>
      <div className="text-sm font-medium">{t('hr.belgeler.upload.drop')}</div>
      <p className="max-w-sm text-xs text-muted-foreground">{t('hr.belgeler.upload.hint')}</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={acceptAttr}
        onChange={(e) => {
          handleFiles(e.target.files)
          e.target.value = ''
        }}
      />
    </div>
  )
}
